import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";

import {
  useGetEnquiriesQuery,
  useUpdateEnquiryStatusMutation,
} from "../toolkitServices/adminTenantApi";

const Badge = ({ children, color }) => (
  <span
    className={`px-2 py-0.5 text-[10px] rounded-full font-semibold ${color}`}
  >
    {children}
  </span>
);

const EnquiryList = () => {
  const { data, isLoading, error } = useGetEnquiriesQuery();
  const [updateStatus] = useUpdateEnquiryStatusMutation();

  const enquiries = data?.data || [];

  /* ------------------- update status ------------------- */
  const handleStatusChange = async (id, status) => {
    try {
      await updateStatus({
        enquiryId: id,
        status,
      }).unwrap();

      toast.success("Status updated successfully");
    } catch (err) {
      toast.error(err?.data?.msg || "Failed to update status");
    }
  };

  return (
    <div className="bg-white shadow-lg rounded-2xl flex flex-col  h-[calc(100vh-140px)] md:h-[calc(100vh-70px)]">

      {/* Header */}
      <div className="px-5 py-3 rounded-t-2xl bg-[#9481ce]  text-white">
        <h2 className="text-lg font-semibold">Enquiry Management</h2>
        <p className="text-xs text-violet-100">
          View and manage incoming customer enquiries
        </p>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="min-w-full text-sm text-left">
          <thead className="bg-[#e6e1f5] text-[#4b3d91] uppercase text-xs sticky top-0">
            <tr>
              <th className="px-4 py-2">Store Name</th>
              <th className="px-4 py-2">Mobile</th>
              <th className="px-4 py-2">City</th>
              <th className="px-4 py-2">State</th>
              <th className="px-4 py-2 text-center">Branches</th>
              <th className="px-4 py-2">Description</th>
              <th className="px-4 py-2">Created</th>
              <th className="px-4 py-2 text-center">Status</th>
              <th className="px-4 py-2 text-center">Change</th>
            </tr>
          </thead>

          <tbody className="bg-white text-gray-700">
            <AnimatePresence>
              {enquiries.length ? (
                enquiries.map((e, i) => (
                  <motion.tr
                    key={e._id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2, delay: i * 0.02 }}
                    className="border-t hover:bg-[#f4f0ff] transition"
                  >
                    <td className="px-4 py-2 font-medium">
                      {e.storeName}
                    </td>

                    <td className="px-4 py-2 text-blue-600">
                      <a href={`tel:${e.mobileNumber}`}>
                        {e.mobileNumber}
                      </a>
                    </td>

                    <td className="px-4 py-2">{e.city}</td>
                    <td className="px-4 py-2">{e.state}</td>

                    <td className="px-4 py-2 text-center font-semibold">
                      {e.numberOfBranches}
                    </td>

                    <td className="px-4 py-2 text-xs max-w-[200px] truncate">
                      {e.description || "-"}
                    </td>

                    <td className="px-4 py-2 text-xs">
                      {new Date(e.createdAt).toLocaleDateString()}
                    </td>

                    {/* Status Badge */}
                    <td className="px-4 py-2 text-center">
                      {e.status === "new" && (
                        <Badge color="bg-yellow-100 text-yellow-700">
                          New
                        </Badge>
                      )}
                      {e.status === "contacted" && (
                        <Badge color="bg-blue-100 text-blue-700">
                          Contacted
                        </Badge>
                      )}
                      {e.status === "closed" && (
                        <Badge color="bg-green-100 text-green-700">
                          Closed
                        </Badge>
                      )}
                    </td>

                    {/* Change Status */}
                    <td className="px-4 py-2 text-center">
                      <select
                        onChange={(e2) =>
                          handleStatusChange(e._id, e2.target.value)
                        }
                        defaultValue=""
                        className="border text-xs rounded px-2 py-1"
                      >
                        <option value="" disabled>
                          Select
                        </option>
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="closed">Closed</option>
                      </select>
                    </td>
                  </motion.tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center py-6 text-gray-500">
                    {isLoading
                      ? "Loading enquiries..."
                      : "No enquiries found"}
                  </td>
                </tr>
              )}
            </AnimatePresence>
          </tbody>
        </table>
      </div>

      {error && (
        <div className="text-red-500 text-xs px-4 py-2 border-t">
          Failed to load enquiries
        </div>
      )}
    </div>
  );
};

export default EnquiryList;